<template>
	<main>
		<TopBottomLayout>
			<h6>이달의 포착주</h6>
			<HomeItemLayout v-if="masterListData">
				<MasterList
					v-if="!isMasterListLoading"
					:master-list="masterListData ?? []"
					@update:master-pk="onChangeMasterPk"
				/>
			</HomeItemLayout>
			<HomeItemLayout class="c_responsive">
				<ExpandableTitleBar
					v-if="masterDetailData"
					:title="masterDetailData?.master.master_trading_style ?? ''"
					:sub-title="masterDetailData?.master.master_trading_description ?? ''"
					:has-view="false"
				/>
				<MasterInsightBox v-if="masterDetailData" :item="masterDetailData.master" :sub-yn="subYn" />
			</HomeItemLayout>
			<ListLayout v-if="masterDetailData" class="c_responsive">
				<ExpandableTitleBar
					title="이달의 포착주"
					:time="masterDetailData?.base_date ? `${dayjs(masterDetailData?.base_date).format('YYYY-MM-DD')} 기준` : ''"
					:has-view="false"
				/>

				<div class="list_layout">
					<ShortenBookmarkList
						v-for="(d, i) in masterDetailData?.monthly_capture"
						:key="i"
						:data="d"
						:index="i"
						indexed
						@update:book-mark-refetch="handleBookmarkRefetch"
					/>
				</div>
				<!-- <div v-if="subYn && checkAuthData?.success" class="list_layout">
					<ShortenBookmarkList
						v-for="(d, i) in masterDetailData?.monthly_capture"
						:key="i"
						:data="d"
						:index="i"
						indexed
						@update:book-mark-refetch="handleBookmarkRefetch"
					/>
				</div>
				<div v-else class="list_layout">
					<BlurredBookmarkList
						v-for="(d, i) in masterDetailData?.monthly_capture"
						:key="i"
						:data="d"
						:index="i"
						:blur="i !== Number(masterDetailData?.monthly_capture.length)"
						@update:book-mark-refetch="handleBookmarkRefetch"
					/>
				</div> -->
			</ListLayout>
			<ListLayout class="c_responsive">
				<ExpandableTitleBar title="대가의 투자원칙" :has-view="false" />
				<EditorBox><div v-dompurify-html="masterDetailData?.master.master_investment_rule"></div></EditorBox>
			</ListLayout>
			<ListLayout class="c_responsive">
				<ExpandableTitleBar title="대가의 투자스타일" :has-view="false" />
				<EditorBox><div v-dompurify-html="masterDetailData?.master.master_investment_style"></div></EditorBox>
			</ListLayout>
			<SectorLayout v-if="masterDetailData && masterSectorValue.length > 0" class="c_responsive">
				<ExpandableTitleBar title="대가의 투자 섹터 비중" :has-view="false" />
				<DoughnutChartItem :data="sectorChartData" :date="masterDetailData.base_date" />
			</SectorLayout>
		</TopBottomLayout>
	</main>
</template>
<script setup lang="ts">
// core
import { computed, onBeforeMount, onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';

// api
import { useGetCheckAuthApi } from '@/api/auth';
import { MasterSector, useGetMasterDetailApi_USA, useGetMasterListApi } from '@/api/master';
// component
import DoughnutChartItem from '@/components/atom/chart/DoughnutChartItem.vue';
import ExpandableTitleBar from '@/components/atom/tabBar/ExpandableTitleBar.vue';
import HomeItemLayout from '@/components/home/HomeItemLayout.vue';
import EditorBox from '@/components/layout/common/EditorBox.vue';
import ListLayout from '@/components/layout/main/ListLayout.vue';
import TopBottomLayout from '@/components/layout/main/TopBottomLayout.vue';
import MasterInsightBox from '@/components/masterStock/MasterInsightBox.vue';
import SectorLayout from '@/components/masterStock/SectorLayout.vue';
import MasterList from '@/components/rankList/MasterList.vue';
import ShortenBookmarkList from '@/components/rankList/ShortenBookmarkList.vue';
// store
import { useTokenStore } from '@/stores/token';
import { useModalStore } from '@/stores/modal';

// -----variable-----
const route = useRoute();
const router = useRouter();
const tokenStore = useTokenStore();
const subYn = ref(1);
const masterSectorLabel = ref<string[]>([]);
const masterSectorValue = ref<number[]>([]);

const routePk = computed(() => {
	return route.query.masterPk ?? '';
});
const modalStore = useModalStore();

// -----api-----
const { refetch: refetchCheckAuth } = useGetCheckAuthApi(
	computed(() => ({
		num: 'ALL',
		student: false,
		enable: false,
	}))
);
const { data: masterListData, isLoading: isMasterListLoading } = useGetMasterListApi();

// 미국주식용 API
const { data: masterDetailData, refetch: refetchMasterDetail } = useGetMasterDetailApi_USA(
	computed(() => {
		return {
			master_pk: String(routePk.value),
		};
	})
);

// -----chart-----
const sectorChartData = computed(() => ({
	labels: masterSectorLabel.value,
	datasets: [
		{
			data: masterSectorValue.value,
			backgroundColor: [
				'#01A9FF',
				'#FFB83F',
				'#FF5946',
				'#DBAEFF',
				'#A9F4D0',
				'#2B8B5D',
				'#F49698',
				'#FEFFD5',
				'#CFD096',
				'#EAEAEA',
				'#3E61FF',
			],
			borderColor: 'transparent',
		},
	],
}));

// -----function-----
// 북마크 리패치 함수
const handleBookmarkRefetch = async () => {
	await refetchMasterDetail();
};

// 마스터 클릭 시, 실행 함수
const onChangeMasterPk = async (pk: number) => {
	router.replace({ query: { masterPk: pk } });
	// if (masterListData.value) {
	// 	subYn.value = masterListData.value.master_list_onsub.some((item) => item.master_pk === Number(pk)) ? 1 : 0;
	// }
	masterSectorLabel.value = [];
	masterSectorValue.value = [];
	await fetchMasterDetail();
};

// 기타 상세 정보 불러오는 함수
const fetchMasterDetail = async () => {
	await refetchMasterDetail();

	if (masterDetailData.value) {
		const keys = Object.keys(masterDetailData.value?.master_sector?.ratio ?? {}) as (keyof MasterSector)[];
		const result = keys.filter((key) => (masterDetailData.value?.master_sector?.ratio?.[key] ?? 0) > 0);
		const resultNumber = result.map((key) => masterDetailData.value?.master_sector?.ratio?.[key] ?? 0);
		masterSectorLabel.value = result ?? [];
		masterSectorValue.value = resultNumber ?? [];
	}
};

// 구독 여부 확인 함수
const isSubscribed = (targetPk: number) => {
	return !masterListData.value?.master_list_nosub.some((item) => item.master_pk === targetPk);
};

const handleGetMasterPk = () => {
	if (masterListData.value && masterListData.value?.master_list_onsub.length > 0) {
		router.replace({ query: { masterPk: masterListData.value?.master_list_onsub[0].master_pk } });
		subYn.value = 1;
	} else if (masterListData.value && masterListData.value?.master_list_nosub.length > 0) {
		router.replace({ query: { masterPk: masterListData.value?.master_list_nosub[0].master_pk } });
		subYn.value = 0;
	}
};

// -----composition-----
watchEffect(async () => {
	if (masterListData.value && !routePk.value) {
		handleGetMasterPk();
	}
	if (routePk.value) {
		if (isSubscribed(Number(routePk.value))) {
			subYn.value = 1;
		} else {
			subYn.value = 0;
		}
		await fetchMasterDetail();
	}
});

onMounted(async () => {
	if (tokenStore.isLogin) {
		await refetchCheckAuth();
	}
});

onBeforeMount(async () => {
	const res = await refetchCheckAuth();
	if (!res.data?.success) {
		modalStore.openModal('GuideModal');
	}
});
</script>

<style lang="scss" scoped>
.list_layout {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}
:deep(#doughnut-chart) {
	display: flex;
	justify-content: center;
	position: relative;
	height: 400px; // 고정 높이 설정
	width: 100%;
	max-width: 600px; // 최대 너비 설정
	margin: 0 auto;
}
canvas {
	max-width: 100%;
	width: 100%;
	max-height: 30rem;
	height: auto;
}
@media (max-width: 768px) {
	:deep(#doughnut-chart) {
		height: 300px; // 모바일에서 더 작은 높이
	}
}

.skeleton_loading {
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
}
</style>
